/**
 * Authoring a station's furniture in the station's own frame.
 *
 * A station file says where it stands and which way it faces once, and every box it is built from
 * is then written relative to that: local `+x` is the station's right, local `-z` is the way it
 * faces, which is the frame every other angle in this game already speaks. Without this each file
 * would carry its own sines, and moving a counter half a metre along a wall would mean editing
 * four world positions that only agree by hand.
 */

import { boxCollider } from '@/sim/physics/colliderShapes';
import { degToRad } from '@/sim/math';
import { vec3 } from '@/sim/vec3';
import type { ColliderSpec } from '@/sim/physics/physicsContract';
import type { ImpactSurfaceId } from '@/sim/impacts/impactSurfaceContract';
import type { InteractableId } from '@/sim/world/interactableContract';
import type { Vec3 } from '@/sim/vec3';

/** Where a station stands and which way it faces, before any of its parts exist. */
export interface StationFrame {
  readonly kindId: InteractableId;
  readonly position: Vec3;
  readonly yawDegrees: number;
}

/**
 * One box of a station's furniture.
 *
 * The id is the station's kind and the part's name, so every collider in the hub is named for what
 * it actually is and two stations can carry a `counter` each without colliding in the level's own
 * namespace.
 */
export function stationPart(
  frame: StationFrame,
  part: string,
  local: Vec3,
  halfExtents: Vec3,
  impactSurface: ImpactSurfaceId,
): ColliderSpec {
  const yaw = degToRad(frame.yawDegrees);
  const cos = Math.cos(yaw);
  const sin = Math.sin(yaw);

  return boxCollider({
    id: `${frame.kindId}.${part}`,
    center: vec3(
      frame.position.x + local.x * cos + local.z * sin,
      frame.position.y + local.y,
      frame.position.z - local.x * sin + local.z * cos,
    ),
    halfExtents,
    yawDegrees: frame.yawDegrees,
    impactSurface,
  });
}
